import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { RestService } from '../../../core/services/rest.service';
import {
  ImportValidationGroup,
  toImportValidationView,
} from '../../../core/utils/import-validation-error.util';
import { ExpandableAccordionComponent } from '../../common/expandable-accordion/expandable-accordion.component';
import { ImportValidationErrorPanelComponent } from '../import-validation-error-panel/import-validation-error-panel.component';

export interface ImportPreviewResult {
  addedCount: number;
  suiteId: string;
  suiteName: string;
}

// Matches TcgTestCaseDetailComponent column structure exactly
const HEADER_COLUMNS = [
  { mapping_key: 'userStoryId',   display_name: 'Story ID',     isVisible: true  },
  { mapping_key: 'testCaseId',    display_name: 'Test Case ID', isVisible: true  },
  { mapping_key: 'testCaseType',  display_name: 'Test Type',    isVisible: true  },
  { mapping_key: 'priority',      display_name: 'Priority',     isVisible: true  },
  { mapping_key: 'testCase',      display_name: 'Test Case',    isVisible: true  },
];

const EXPAND_COLUMNS = [
  { mapping_key: 'testCaseType',       display_name: 'Test Type',            isVisible: false },
  { mapping_key: 'subType',            display_name: 'Sub Type',             isVisible: false },
  { mapping_key: 'traceability',       display_name: 'Traceability',         isVisible: false },
  { mapping_key: 'prerequisites',      display_name: 'Prerequisites',        isVisible: false },
  { mapping_key: 'testScenario',       display_name: 'Test Steps',           isVisible: false },
  { mapping_key: 'sanitizedTestSteps', display_name: 'Sanitized Test Steps', isVisible: false },
  { mapping_key: 'expectedResult',     display_name: 'Expected Results',     isVisible: false },
];

const ALL_COLUMNS = [...HEADER_COLUMNS, ...EXPAND_COLUMNS];

function mapToRow(tc: any): any {
  const storyId = tc.key ?? tc.userStoryId ?? tc.storyId ?? '';
  return {
    ...tc,
    userStoryId:  storyId,
    key:          storyId,
    testCaseId:   tc.testCaseId ?? tc.id ?? '',
    testCase:     tc.testCase ?? tc.title ?? tc.name ?? tc.summary ?? '',
    testScenario: tc.testScenario ?? tc.steps ?? '',
    sanitizedTestSteps: tc.sanitizedTestSteps ?? '',
    expectedResult: tc.expectedResult ?? tc.expectedResults ?? '',
    testCaseType: tc.testCaseType ?? tc.testType ?? tc.type ?? '',
    subType:      tc.subType ?? tc.subtype ?? '',
    priority:     tc.priority ?? '',
    traceability: tc.traceability ?? '',
    source:       tc.source ?? 'EXCEL',
  };
}

@Component({
  selector: 'app-import-preview-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ExpandableAccordionComponent,
    ImportValidationErrorPanelComponent,
  ],
  templateUrl: './import-preview-modal.component.html',
  styleUrl: './import-preview-modal.component.scss',
})
export class ImportPreviewModalComponent implements OnInit, OnDestroy {
  activeModal = inject(NgbActiveModal);
  isActive = false;

  @Input() extractedTestCases: any[] = [];
  @Input() suiteId = '';
  @Input() suiteName = '';
  @Input() projectId = '';
  @Input() llmConnectionId: string | null = null;
   /** File connection id from the import step. */
   @Input() connectionId = '';
   /** File type from the import step ('xlsx' | 'csv'). */
   @Input() documentType = 'xlsx';

  searchQuery = '';
  isAdding = false;
  addError = '';
  validationErrors: ImportValidationGroup[] = [];
  validationSummary = '';
  duplicateNote = '';
  selectedRows: any[] = [];

  accordionData: { columns: any[]; rows: any[] } = { columns: ALL_COLUMNS, rows: [] };

  @ViewChild('errorPanel') private errorPanel?: ElementRef<HTMLElement>;

  private allRows: any[] = [];
  private errorScrollTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly restService: RestService) {}

  ngOnInit(): void {
    this.allRows = this.extractedTestCases.map(mapToRow);
    this.applyFilter();
  }

  ngOnDestroy(): void {
    if (this.errorScrollTimer) {
      clearTimeout(this.errorScrollTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get origin(): string {
    if (this.documentType === 'csv') {
      return 'CSV';
    }
    if (this.documentType === 'zephyr') {
      return 'ZEPHYR_ESSENTIAL';
    }
    return 'EXCEL';
  }

  // ── Search ─────────────────────────────────────────────────────────────────

  onSearchChange(): void {
    this.applyFilter();
  }

  private applyFilter(): void {
    const q = this.searchQuery.trim().toLowerCase();
    const rows = q
      ? this.allRows.filter(
          (r) =>
            (r.testCaseId ?? '').toLowerCase().includes(q) ||
            (r.testCase ?? '').toLowerCase().includes(q) ||
            (r.userStoryId ?? '').toLowerCase().includes(q)
        )
      : [...this.allRows];
    this.accordionData = { columns: ALL_COLUMNS, rows };
    // Reset selection when filter changes
    this.selectedRows = [];
  }

  // ── Accordion events ───────────────────────────────────────────────────────

  onAccordionAction(event: any): void {
    if (event?.type === 'row_select') {
      this.selectedRows = event.data ?? [];
    }
  }

  // ── Add action ─────────────────────────────────────────────────────────────

  get canAdd(): boolean {
    return this.selectedRows.length > 0 && !this.isAdding;
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onAddToSuite(): void {
    if (!this.canAdd || !this.suiteId || !this.connectionId) return;

    this.isAdding = true;
    this.clearAddError();

    this.restService
      .addImportedTestCasesToTestSuite(
        this.projectId,
        this.suiteId,
        this.origin,
        this.connectionId,
        this.selectedRows
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isAdding = false;
          this.activeModal.close({
            addedCount: this.selectedRows.length,
            suiteId: this.suiteId,
            suiteName: this.suiteName,
          } as ImportPreviewResult);
        },
        error: (err: unknown) => {
          this.isAdding = false;
          this.applyAddError(err, 'Failed to add test cases. Please try again.');
        },
      });
  }

  private applyAddError(err: unknown, fallback: string): void {
    const view = toImportValidationView(err, fallback);
    this.validationErrors = view.groups;
    this.validationSummary = view.summary;
    this.duplicateNote = view.duplicateNote;
    this.addError = view.genericMessage;
    this.scrollErrorsIntoView();
  }

  private scrollErrorsIntoView(): void {
    if (!this.validationErrors.length && !this.addError) {
      return;
    }
    if (this.errorScrollTimer) {
      clearTimeout(this.errorScrollTimer);
    }
    this.errorScrollTimer = setTimeout(() => {
      const target = this.errorPanel?.nativeElement;
      if (!target) {
        return;
      }
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({
        block: 'nearest',
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    });
  }

  private clearAddError(): void {
    this.addError = '';
    this.validationErrors = [];
    this.validationSummary = '';
    this.duplicateNote = '';
  }


  // onAdd(): void {
  //   if (!this.canAdd) return;

  //   const payload = {
  //     versionName: this.suiteName,
  //     selectedVersionId: this.suiteId,
  //     llmConnectionId: this.llmConnectionId,
  //     isNewTestSuite: false,
  //     testCases: this.selectedRows.map((tc: any) => ({
  //       _id: tc._id,
  //       testCaseId: tc.testCaseId ?? '',
  //       testCase: tc.testCase ?? '',
  //       testScenario: tc.testScenario ?? '',
  //       expectedResult: tc.expectedResult ?? '',
  //       prerequisites: tc.prerequisites ?? '',
  //       steps: tc.steps ?? tc.testScenario ?? '',
  //       key: tc.key ?? tc.userStoryId ?? '',
  //       storydesc: tc.storydesc ?? tc.storyDesc ?? '',
  //       storysummary: tc.storysummary ?? tc.storySummary ?? '',
  //       acceptancecriteria: tc.acceptancecriteria ?? tc.acceptanceCriteria ?? '',
  //       testCaseType: tc.testCaseType ?? '',
  //       subType: tc.subType ?? '',
  //       traceability: tc.traceability ?? '',
  //       priority: tc.priority ?? '',
  //     })),
  //   };
  // }

}
